import { useEffect, useState } from "react"
import { Head, router } from "@inertiajs/react"
import { AdminShell } from "@/components/AdminShell"
import { Button } from "@/components/ui/button"
import { Radio, ChevronDown, ChevronUp, Copy, Check, Loader2 } from "lucide-react"

const MONTHS = [
  { value: 1,  label: "January" },
  { value: 2,  label: "February" },
  { value: 3,  label: "March" },
  { value: 4,  label: "April" },
  { value: 5,  label: "May" },
  { value: 6,  label: "June" },
  { value: 7,  label: "July" },
  { value: 8,  label: "August" },
  { value: 9,  label: "September" },
  { value: 10, label: "October" },
  { value: 11, label: "November" },
  { value: 12, label: "December" },
]

type Region = { id: number; name: string }

type FormValues = {
  start_month: number
  start_day:   number
  end_month:   number
  end_day:     number
  region_id:   string | null
  city:        string | null
  length_hint: string | null
  focus:       string | null
}

type SourceSummary = {
  birthdays:      number
  deaths:         number
  recordings:     number
  chart_listings: number
  gigs:           number
}

type BulletsResult = {
  bullets_text:   string
  source_summary: SourceSummary
  thin_data:      boolean
}

type ScriptResult = {
  script: string
}

type Props = {
  regions:      Region[]
  style_prompt: string | null
  bullets:      BulletsResult | null
  result:       ScriptResult | null
  form:         FormValues | null
  error:        string | null
}

const ic = "w-full rounded border border-hairline bg-page px-3 py-1.5 text-sm text-ink-body placeholder:text-ink-muted focus:outline-none focus:ring-1 focus:ring-current"
const sc = "w-full rounded border border-hairline bg-page px-3 py-1.5 text-sm text-ink-body focus:outline-none focus:ring-1 focus:ring-current"

function dayOptions() {
  return Array.from({ length: 31 }, (_, i) => i + 1)
}

export default function RadioScript({ regions, style_prompt, bullets, result, form, error }: Props) {
  const today = new Date()
  const defaultForm: FormValues = {
    start_month: form?.start_month ?? (today.getMonth() + 1),
    start_day:   form?.start_day   ?? today.getDate(),
    end_month:   form?.end_month   ?? (today.getMonth() + 1),
    end_day:     form?.end_day     ?? Math.min(today.getDate() + 6, 31),
    region_id:   form?.region_id   ?? null,
    city:        form?.city        ?? null,
    length_hint: form?.length_hint ?? null,
    focus:       form?.focus       ?? "Priority: 1. Birthdays & passings, 2. Gigs, 3. First chartings, 4. Chart listings",
  }

  const [values, setValues]               = useState<FormValues>(defaultForm)
  const [editedBullets, setEditedBullets] = useState(bullets?.bullets_text ?? "")
  const [generatingFacts, setGeneratingFacts]   = useState(false)
  const [generatingScript, setGeneratingScript] = useState(false)
  const [promptOpen, setPromptOpen]       = useState(false)
  const [styleText, setStyleText]         = useState(style_prompt ?? "")
  const [savingPrompt, setSavingPrompt]   = useState(false)
  const [copied, setCopied]               = useState(false)

  // Sync editable textarea when new bullets arrive from the server
  useEffect(() => {
    setEditedBullets(bullets?.bullets_text ?? "")
  }, [bullets?.bullets_text])

  function set(key: keyof FormValues, val: string | number | null) {
    setValues((prev) => ({ ...prev, [key]: val }))
  }

  function handleGenerateFacts(e: React.FormEvent) {
    e.preventDefault()
    if (generatingFacts) return
    setGeneratingFacts(true)
    router.post("/admin/radio_script/facts", {
      start_month: values.start_month,
      start_day:   values.start_day,
      end_month:   values.end_month,
      end_day:     values.end_day,
      region_id:   values.region_id ?? "",
      city:        values.city ?? "",
    }, {
      preserveScroll: false,
      onFinish: () => setGeneratingFacts(false),
    })
  }

  function handleGenerateScript() {
    if (generatingScript || !editedBullets.trim()) return
    setGeneratingScript(true)
    router.post("/admin/radio_script", {
      facts_text:  editedBullets,
      start_month: values.start_month,
      start_day:   values.start_day,
      end_month:   values.end_month,
      end_day:     values.end_day,
      region_id:   values.region_id ?? "",
      city:        values.city ?? "",
      length_hint: values.length_hint ?? "",
      focus:       values.focus ?? "",
    }, {
      preserveScroll: false,
      onFinish: () => setGeneratingScript(false),
    })
  }

  async function saveStylePrompt() {
    setSavingPrompt(true)
    try {
      const csrf = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? ""
      await fetch("/admin/site_settings", {
        method: "PATCH",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          "X-CSRF-Token": csrf,
        },
        body: new URLSearchParams({ radio_script_style_prompt: styleText }).toString(),
        redirect: "manual",
      })
    } finally {
      setSavingPrompt(false)
    }
  }

  function copyScript() {
    if (!result?.script) return
    navigator.clipboard.writeText(result.script).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  return (
    <>
      <Head title="Radio Script Generator">
        <meta name="description" content="Generate a radio script for This Week in Australian Music History." />
        <meta property="og:title" content="Radio Script Generator" />
        <meta property="og:description" content="Generate a radio script for This Week in Australian Music History." />
      </Head>
      <AdminShell>
        <div className="max-w-3xl">
          <div className="flex items-center gap-2 mb-6">
            <Radio className="h-5 w-5 text-ink-muted" />
            <h1>Radio Script Generator</h1>
          </div>
          <p className="text-sm text-ink-muted mb-6">
            "This Week in Australian Music History" — pick a date range and region, then generate facts to review and edit before asking the AI to write the final script.
          </p>

          {/* Step 1 — date range & region form */}
          <form onSubmit={handleGenerateFacts} className="space-y-5">
            <fieldset>
              <legend className="text-sm font-medium text-ink-body mb-2">Date range</legend>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-ink-muted mb-1">From</label>
                  <div className="flex gap-2">
                    <select value={values.start_day} onChange={(e) => set("start_day", parseInt(e.target.value))} className={sc}>
                      {dayOptions().map((d) => <option key={d} value={d}>{d}</option>)}
                    </select>
                    <select value={values.start_month} onChange={(e) => set("start_month", parseInt(e.target.value))} className={sc}>
                      {MONTHS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-ink-muted mb-1">To</label>
                  <div className="flex gap-2">
                    <select value={values.end_day} onChange={(e) => set("end_day", parseInt(e.target.value))} className={sc}>
                      {dayOptions().map((d) => <option key={d} value={d}>{d}</option>)}
                    </select>
                    <select value={values.end_month} onChange={(e) => set("end_month", parseInt(e.target.value))} className={sc}>
                      {MONTHS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                    </select>
                  </div>
                </div>
              </div>
            </fieldset>

            <div>
              <label className="block text-sm font-medium text-ink-body mb-1">Region</label>
              <div className="grid grid-cols-2 gap-3">
                <select
                  value={values.region_id ?? ""}
                  onChange={(e) => { set("region_id", e.target.value || null); set("city", null) }}
                  className={sc}
                >
                  <option value="">Any region</option>
                  {regions.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                </select>
                <input
                  type="text"
                  placeholder="or type a city…"
                  value={values.city ?? ""}
                  onChange={(e) => { set("city", e.target.value || null); set("region_id", null) }}
                  className={ic}
                />
              </div>
            </div>

            <Button type="submit" disabled={generatingFacts}>
              {generatingFacts && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {generatingFacts ? "Fetching facts…" : "Step 1 — Get facts"}
            </Button>
          </form>

          {/* Error */}
          {error && (
            <div className="mt-6 rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/30 p-4">
              <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
            </div>
          )}

          {/* Step 2 — editable bullets */}
          {bullets && (
            <div className="mt-8">
              <div className="mb-3">
                <p className="text-xs text-ink-muted mb-1" style={{ fontFamily: "'IBM Plex Mono', ui-monospace, monospace" }}>
                  Based on: {bullets.source_summary.birthdays} birthday{bullets.source_summary.birthdays !== 1 ? "s" : ""},&nbsp;
                  {bullets.source_summary.deaths} death{bullets.source_summary.deaths !== 1 ? "s" : ""},&nbsp;
                  {bullets.source_summary.recordings} first charting{bullets.source_summary.recordings !== 1 ? "s" : ""},&nbsp;
                  {bullets.source_summary.chart_listings} chart listing{bullets.source_summary.chart_listings !== 1 ? "s" : ""},&nbsp;
                  {bullets.source_summary.gigs} gig{bullets.source_summary.gigs !== 1 ? "s" : ""}
                </p>
              </div>

              {bullets.thin_data && (
                <div className="mb-3 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30 p-3">
                  <p className="text-sm text-amber-700 dark:text-amber-400">
                    Very little data was found. Consider widening the date range or removing the region filter.
                  </p>
                </div>
              )}

              <label className="block text-sm font-medium text-ink-body mb-2">
                Edit facts before generating script
              </label>
              <textarea
                value={editedBullets}
                onChange={(e) => setEditedBullets(e.target.value)}
                rows={Math.max(10, editedBullets.split("\n").length + 2)}
                className="w-full rounded border border-hairline bg-page px-3 py-2 text-sm text-ink-body font-mono leading-relaxed focus:outline-none focus:ring-1 focus:ring-current"
              />

              {/* Step 2 optional fields */}
              <div className="mt-3 grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-ink-body mb-1">
                    Length target <span className="text-ink-muted font-normal">(optional)</span>
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. 3 minutes, 400 words"
                    value={values.length_hint ?? ""}
                    onChange={(e) => set("length_hint", e.target.value || null)}
                    className={ic}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-ink-body mb-1">
                    Focus <span className="text-ink-muted font-normal">(optional)</span>
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. emphasise the birthdays"
                    value={values.focus ?? ""}
                    onChange={(e) => set("focus", e.target.value || null)}
                    className={ic}
                  />
                </div>
              </div>

              <div className="mt-4">
                <Button
                  type="button"
                  onClick={handleGenerateScript}
                  disabled={generatingScript || !editedBullets.trim()}
                >
                  {generatingScript && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  {generatingScript ? "Writing script…" : "Step 2 — Generate script"}
                </Button>
              </div>
            </div>
          )}

          {/* Script result */}
          {result && (
            <div className="mt-8">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm font-medium text-ink-body">Script</p>
                <button
                  type="button"
                  onClick={copyScript}
                  className="flex items-center gap-1.5 text-xs text-ink-muted hover:text-ink-body transition-colors"
                >
                  {copied ? <Check className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5" />}
                  {copied ? "Copied" : "Copy"}
                </button>
              </div>
              <div className="rounded-lg border border-hairline bg-surface p-5">
                <pre className="whitespace-pre-wrap text-sm text-ink-body leading-relaxed" style={{ fontFamily: "inherit" }}>
                  {result.script}
                </pre>
              </div>
            </div>
          )}

          {/* Voice prompt editor */}
          <div className="mt-10 border-t border-hairline pt-6">
            <button
              type="button"
              onClick={() => setPromptOpen((o) => !o)}
              className="flex items-center gap-2 text-sm font-medium text-ink-muted hover:text-ink-body transition-colors"
            >
              {promptOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              Edit voice &amp; style prompt
            </button>
            {promptOpen && (
              <div className="mt-3 space-y-3">
                <p className="text-xs text-ink-muted">
                  This system prompt is sent to the AI in Step 2. Edit it to tune the voice, length, or style of generated scripts.
                </p>
                <textarea
                  value={styleText}
                  onChange={(e) => setStyleText(e.target.value)}
                  rows={8}
                  className="w-full rounded border border-hairline bg-page px-3 py-2 text-sm text-ink-body focus:outline-none focus:ring-1 focus:ring-current"
                />
                <Button type="button" onClick={saveStylePrompt} disabled={savingPrompt}>
                  {savingPrompt ? "Saving…" : "Save prompt"}
                </Button>
              </div>
            )}
          </div>
        </div>
      </AdminShell>
    </>
  )
}
